import React, { forwardRef, useMemo } from 'react';
import AnimatedSvg from './components/AnimatedSvg';
import { addAttributesToStringSvg } from './utils';
import classNames from 'classnames';
import styles from './AnimatedIcon.module.scss';
import { useAnimatedIconInteraction } from './useAnimatedIconInteraction';
import type { AnimatedSvgRef, AnimatedIconBuilderProps } from './types';
import { DEFAULT_ANIMATION_DURATION } from './constants';

export type { AnimatedIconBuilderProps as AnimatedIconProps } from './types';

const AnimatedIcon = forwardRef<AnimatedSvgRef, AnimatedIconBuilderProps>(
  (props, ref) => {
    const {
      svg,
      duration = DEFAULT_ANIMATION_DURATION,
      className,
      id,
      'data-testid': dataTestId,
      reducedMotion,
      trigger,
    } = props;
    const svgContent = svg?.svgContent;

    const processedSvg = useMemo(
      () => addAttributesToStringSvg(svgContent || '', duration),
      [svgContent, duration],
    );

    const { actualRef, isClickable, ...eventProps } =
      useAnimatedIconInteraction(trigger, ref);

    if (svgContent) {
      const commonProps = {
        className: classNames(
          styles.animatedIcon,
          'animatedIcon',
          'icon',
          className,
        ),
        id,
        'data-testid': dataTestId,
        ...eventProps,
      };

      const animatedIcon = (
        <AnimatedSvg
          ref={actualRef}
          svgContent={processedSvg}
          reducedMotion={reducedMotion}
          duration={duration}
        />
      );

      if (isClickable) {
        return (
          <button type="button" aria-label="Animated icon" {...commonProps}>
            {animatedIcon}
          </button>
        );
      }

      return <div {...commonProps}>{animatedIcon}</div>;
    }

    return null;
  },
);

export default AnimatedIcon;
